import React from 'react'
import PropTypes from 'prop-types'
import cx from 'classnames'
import ReactGA from 'react-ga'

import isEqual from 'lodash/isEqual'
import { useCreditCard } from '../../CreditCardProvider/CreditCardProvider'
import Button from '../../Button/Button'
import { ConfirmModal } from '../../ConfirmModal/ConfirmModal'
import Error from '../../Error/Error'

import AMEX from '../../../assets/credit-cards/amex.svg'
import GENERIC from '../../../assets/credit-cards/credit-card.svg'
import DISCOVER from '../../../assets/credit-cards/discover.svg'
import MASTERCARD from '../../../assets/credit-cards/mastercard.svg'
import VISA from '../../../assets/credit-cards/visa.svg'

import './SavedCreditCardLabel.css'
import { toast } from 'react-toastify'

const cardTypeMap = {
  AMEX,
  DISCOVER,
  MASTERCARD,
  VISA
}

export const SavedCreditCardLabel = React.memo(({ card, isDeletable }) => {
  const [showModal, setShowModal] = React.useState(false)
  const { deleteCard, loading } = useCreditCard()
  const CardIcon = cardTypeMap[card.cardType] || GENERIC
  const input = {
    variables: {
      input: {
        cardGuid: card.guid
      }
    }
  }

  const { expired, isPrimary } = card
  const expirationString = expired ? 'Expired' : 'Expires'
  return (
    <span styleName={cx('card', { expired })}>
      <div data-testid='credit-card-info' styleName='cardInfo'>
        <CardIcon styleName='cardIcon' />
        <div>
          <p data-testid='credit-card-number' styleName='cardNumber'>
            {card.maskedPan.replace(/XXXX-/, '**** ')}
          </p>
          <p data-testid='credit-card-exp'styleName='cardExp'>
            <strong>{expirationString}</strong> {card.expirationMonth}/
            {card.expirationYear}
          </p>
        </div>
      </div>
      {isPrimary && <div data-testid='credit-card-primary' styleName='primary'>Primary</div>}
      {isDeletable && (
        <Button
          data-testid='button-delete-cc'
          type='button'
          onClick={event => {
            event.stopPropagation()
            setShowModal(true)
          }}
        >
          DELETE
        </Button>
      )}
      {showModal &&
        <ConfirmModal
          header='Delete Credit Card'
          onClose={() => setShowModal(false)}
          onConfirm={async () => {
            await deleteCard.mutate(input)
            toast('Card Deleted Successfully!')
            ReactGA.event({
              category: 'User Account',
              action: 'Delete Credit Card'
            })
          }}
          confirmText='Delete Credit Card'
          isLoading={Boolean(loading)}>
          {deleteCard.result.error && <Error error={deleteCard.result.error} />}
          <div>Are you sure you want to delete this card?</div>
          {isPrimary && <div>This is currently your primary credit card!</div>}
        </ConfirmModal>
      }
    </span>
  )
}, isEqual)

const CardShape = PropTypes.shape({
  cardType: PropTypes.string.isRequired,
  expirationMonth: PropTypes.string.isRequired,
  expirationYear: PropTypes.string.isRequired,
  isPrimary: PropTypes.bool,
  maskedPan: PropTypes.string.isRequired,
  expired: PropTypes.bool.isRequired
})

SavedCreditCardLabel.propTypes = {
  card: CardShape.isRequired,
  isDeletable: PropTypes.bool
}
